export type LogLevel = "debug" | "info" | "warn" | "error" | "none";

export type LogContext = {
	event?: string;
	module?: string;
	data?: Record<string, unknown>;
	error?: unknown;
	sampleRate?: number;
};

type Logger = {
	debug: (message: string, context?: LogContext) => void;
	info: (message: string, context?: LogContext) => void;
	warn: (message: string, context?: LogContext) => void;
	error: (message: string, context?: LogContext) => void;
	child: (context: LogContext) => Logger;
};

const LOG_LEVELS: Record<Exclude<LogLevel, "none">, number> = {
	debug: 10,
	info: 20,
	warn: 30,
	error: 40,
};

const REDACT_KEYS = [
	/token/i,
	/password/i,
	/secret/i,
	/authorization/i,
	/cookie/i,
	/session/i,
	/private/i,
	/principal/i,
	/address/i,
	/wallet/i,
	/mnemonic/i,
];

const REDACT_VALUE = "[REDACTED]";
const MAX_REDACT_DEPTH = 4;

const DEFAULT_LEVEL: LogLevel =
	process.env.NODE_ENV === "development" ? "debug" : "info";

const isServer = () => typeof window === "undefined";

const normalizeLogLevel = (level: string | undefined): LogLevel => {
	if (!level) return DEFAULT_LEVEL;
	const normalized = level.toLowerCase();
	if (
		normalized === "debug" ||
		normalized === "info" ||
		normalized === "warn" ||
		normalized === "error" ||
		normalized === "none"
	) {
		return normalized as LogLevel;
	}
	return DEFAULT_LEVEL;
};

const readLocalLogLevel = (): string | undefined => {
	if (isServer()) return undefined;
	try {
		return localStorage.getItem("APP_LOG_LEVEL") || undefined;
	} catch (error) {
		return undefined;
	}
};

const resolveLogLevel = (): LogLevel => {
	const envLevel = isServer()
		? process.env.APP_LOG_LEVEL
		: process.env.NEXT_PUBLIC_APP_LOG_LEVEL;
	const localOverride = readLocalLogLevel();
	return normalizeLogLevel(localOverride ?? envLevel);
};

const shouldLog = (level: Exclude<LogLevel, "none">, context?: LogContext) => {
	const configuredLevel = resolveLogLevel();
	if (configuredLevel === "none") return false;
	const threshold = LOG_LEVELS[configuredLevel as Exclude<LogLevel, "none">];
	if (LOG_LEVELS[level] < threshold) return false;

	const sampleRate = context?.sampleRate;
	if (typeof sampleRate === "number" && sampleRate >= 0 && sampleRate < 1) {
		return Math.random() < sampleRate;
	}

	return true;
};

const normalizeError = (error: unknown) => {
	if (!error) return undefined;
	if (error instanceof Error) {
		return {
			name: error.name,
			message: error.message,
			stack: error.stack,
		};
	}
	if (typeof error === "string") {
		return {
			message: error,
		};
	}
	return {
		message: "Unknown error",
		data: error,
	};
};

const shouldRedactKey = (key: string) =>
	REDACT_KEYS.some((pattern) => pattern.test(key));

const redactValue = (value: unknown, depth = 0): unknown => {
	if (depth > MAX_REDACT_DEPTH) return value;
	if (Array.isArray(value)) {
		return value.map((item) => redactValue(item, depth + 1));
	}
	if (value && typeof value === "object") {
		const entries = Object.entries(value as Record<string, unknown>);
		const redacted: Record<string, unknown> = {};
		for (const [key, entryValue] of entries) {
			redacted[key] = shouldRedactKey(key)
				? REDACT_VALUE
				: redactValue(entryValue, depth + 1);
		}
		return redacted;
	}
	return value;
};

const formatPayload = (level: Exclude<LogLevel, "none">, message: string, context?: LogContext) => {
	const payload = {
		level,
		time: new Date().toISOString(),
		message,
		event: context?.event,
		module: context?.module,
		data: context?.data,
		error: normalizeError(context?.error),
	};

	return redactValue(payload) as typeof payload;
};

const writeLog = (
	level: Exclude<LogLevel, "none">,
	message: string,
	context?: LogContext,
) => {
	if (!shouldLog(level, context)) return;
	const payload = formatPayload(level, message, context);
	const consoleRef = globalThis.console;
	const target =
		consoleRef?.[level as "debug" | "info" | "warn" | "error"] ||
		consoleRef?.log ||
		(() => {});
	if (isServer()) {
		try {
			target(JSON.stringify(payload));
		} catch (error) {
			target(
				JSON.stringify({
					level: "error",
					time: new Date().toISOString(),
					message: "Failed to serialize log payload",
					error: normalizeError(error),
				}),
			);
		}
	} else {
		target(payload);
	}
};

export const createLogger = (baseContext: LogContext = {}): Logger => {
	const mergeContext = (context?: LogContext): LogContext => {
		if (!context) return baseContext;
		return {
			...baseContext,
			...context,
			data: {
				...(baseContext.data || {}),
				...(context.data || {}),
			},
		};
	};

	return {
		debug: (message, context) => writeLog("debug", message, mergeContext(context)),
		info: (message, context) => writeLog("info", message, mergeContext(context)),
		warn: (message, context) => writeLog("warn", message, mergeContext(context)),
		error: (message, context) => writeLog("error", message, mergeContext(context)),
		child: (context) => createLogger(mergeContext(context)),
	};
};

export const logger = createLogger();
